import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getSchedule } from 'store/attendance/schedule'
import { BasePathProvider, useSession } from 'components/Providers'
import ScheduleTable from 'components/Schedule'
import { Box, Card, LinearProgress } from '@mui/material'
import PageHeader from 'components/AppBar/PageHeader'
import Header from 'components/Header'
import MarkingPeriodField from 'components/MarkingPeriodField'
import NoRecords from 'components/NoRecords'
import usePrintArea from 'utils/print'

const baseUrl = '/academic/schedule'

export const Schedule = () => {
  const dispatch = useDispatch()
  const {person: { firstName, lastName, type }} = useSession()

  const session = useSelector(state => state.session)
  const personMemberId = session.id
  const parentStudentId = useSelector(state => state.session.studentMemberId);

  const [studentCourseSessionEnrollment, setStudentCourseSessionEnrollment] = useState()
  const [scheduledSessions, setScheduledSessions] = useState()
  const [unScheduledSessions, setUnscheduledSessions] = useState()
  const [startRenderList, setStartRenderList] = useState()
  const [errorMessage, setErrorMessage] = useState()
  const academicYear = useSelector(state => state.session.academicYear)
  const isLoading = useSelector(state => state.attendance.schedule.loading)
  const currentMarkingPeriodId = useSelector(state => state.lookup.currentMarkingPeriodId)

  const { printRef, print } = usePrintArea({
    orientation: 'landscape',
    title: `Schedule - ${firstName ?? ''} ${lastName ?? ''}`.trim(),
  })

  const studentCourseSessionEnrollmentFetch = async () => {
    const isParent = type === 'parent';
    const memberId = isParent ? parentStudentId : personMemberId;
    const personType = isParent ? 'student' : type;

    if (!memberId || !personType || !currentMarkingPeriodId) {
      setStudentCourseSessionEnrollment([]);
      setErrorMessage(<NoRecords message="Loading or missing member information" />);
      return;
    }

    try {
      const action = getSchedule({
        memberId,
        type: personType,
        markingPeriodId: currentMarkingPeriodId,
      });
      const data = await dispatch(action);

      if (data?.payload?.length > 0) {
        setStudentCourseSessionEnrollment(data.payload);
        setErrorMessage('');
      } else {
        setStudentCourseSessionEnrollment([]);
        setErrorMessage(<NoRecords message="No Assigned Courses" />);
      }
    } catch (error) {
      setStudentCourseSessionEnrollment([]);
      setErrorMessage(<NoRecords message="Error loading courses" />);
    }
  };

  useEffect(() => {
    if (currentMarkingPeriodId) {
      studentCourseSessionEnrollmentFetch();
    }
  }, [currentMarkingPeriodId, parentStudentId, personMemberId, type, dispatch]);

  useEffect(() => {
    if (studentCourseSessionEnrollment?.length > 0) {
      let temp = []
      const rawScheduledSessions = studentCourseSessionEnrollment?.filter(
        item => item.courseSession?.schedules?.length > 0,
      )
      rawScheduledSessions.forEach(item => {
        item.courseSession.schedules.forEach(scheduleItem => {
          const newItem = {
            id: item.courseSession.id,
            name: item.courseSession.name,
            startTime: scheduleItem.period.startTime,
            endTime: scheduleItem.period.endTime,
            dayOfWeek: scheduleItem.day,
            periodId: scheduleItem.period.id,
            room: item?.courseSession?.room?.id
              ? item?.courseSession?.room
              : null,
            teachers: item?.courseSession?.teachers
              ? item.courseSession.teachers.map(teacher =>
                `${teacher.lastName}, ${teacher.firstName}`
              ).join(' | ')
              : null,
          }
          temp = addSessionToSchedule(newItem, temp)
        })
      })
      setScheduledSessions(temp)
      setErrorMessage('')
      setUnscheduledSessions(
        studentCourseSessionEnrollment?.filter(item => item.courseSession?.schedules?.length === 0),
      )

    } else if (studentCourseSessionEnrollment?.length === 0) {
      setErrorMessage(<NoRecords message="No Assigned Courses" />)
    }
  }, [studentCourseSessionEnrollment])

  const addSessionToSchedule = (newItem, storage) => {
    if (storage.length === 0) {
      storage.push({
        startTime: newItem.startTime,
        endTime: newItem.endTime,
        dayOfWeek: newItem.dayOfWeek,
        periodId: newItem.periodId,
        list: [{ id: newItem.id, name: newItem.name, room: newItem.room, teachers: newItem.teachers }],
      })
      return storage
    }
    let existsIndex = -1
    storage.forEach((item, index) => {
      if (
        item.startTime === newItem.startTime &&
        item.endTime === newItem.endTime &&
        item.dayOfWeek === newItem.dayOfWeek &&
        item.periodId === newItem.periodId
      ) {
        if (existsIndex === -1) existsIndex = index
      }
    })

    if (existsIndex === -1) {
      storage.push({
        startTime: newItem.startTime,
        endTime: newItem.endTime,
        dayOfWeek: newItem.dayOfWeek,
        periodId: newItem.periodId,
        list: [{ id: newItem.id, name: newItem.name, room: newItem.room, teachers: newItem.teachers }],
      })
      return storage
    } else {
      const temp = storage[existsIndex]
      temp.list.push({ id: newItem.id, name: newItem.name, room: newItem.room, teachers: newItem.teachers })
      storage[existsIndex] = temp
      return storage
    }
  }

  useEffect(() => {
    setStartRenderList(true)
  }, [scheduledSessions])

  if (!personMemberId) return <LinearProgress color="primary" />

  return (
    <BasePathProvider value={baseUrl}>
      <PageHeader title={'Schedule'} />
      <Card sx={{ mt: 2 }} className="schedule-table" ref={printRef}>
        <Box sx={{ pr: 0.5 }}>
          <Header print printTitle={`Schedule of ${firstName} ${lastName}`} printfunc={print} />
        </Box>
        <Box className="print-hide">
          <MarkingPeriodField
            label="Marking Period"
            academicYearId={academicYear?.id}
          />
        </Box>
        <Box sx={{ pr: 10, overflowX: 'auto' }}>
          {isLoading ? (
            <LinearProgress sx={{ m: 2 }} color="primary" />
          ) : startRenderList && scheduledSessions && !errorMessage ? (
            <ScheduleTable
              unScheduledSessions={unScheduledSessions}
              scheduledSessions={scheduledSessions}
            />
          ) : errorMessage ? (
            <Box sx={{ m: 4 }}>{errorMessage}</Box>
          ) : (
            startRenderList === false && <LinearProgress sx={{ m: 2 }} color="success" />
          )}
        </Box>

      </Card>
    </BasePathProvider>
  )
}
